import { GetOneParams, GetOneResult, UpdateParams } from "react-admin";
import { z } from "zod";
import { getAuthenticatedServerClient, schemas } from "../api/server.api";
import { AbstractDataProvider } from "./AbstractDataProvider";

type RecommendationSettingsDto = z.infer<typeof schemas.RecommendationSettingsDto>;

export type RecommendationSettings = {
  id: string;
  maxLessonUnitsPerWeek: number;
  maxLessonUnitsPerDay: number;
  maxDaysBookableInAdvance: number;
  minHoursInAdvance: number;
  /** in minutes */
  pauseDuration: number;
  /** in minutes */
  drivingLessonDuration: number;
  allowOverbooking: boolean;
} & Pick<RecommendationSettingsDto, "vehicles">;

class InstructorRecommendationSettingsProvider extends AbstractDataProvider<RecommendationSettings> {
  async getOne(_: string, { id }: GetOneParams): Promise<GetOneResult<RecommendationSettings>> {
    const serverClient = await getAuthenticatedServerClient();
    const dto = await serverClient.getInstructorRecommendationSettings({ params: { id } });
    const {
      maxDrivingLessonsPerWeek,
      maxDrivingLessonLUsPerDay,
      preferredDurationMinsPerDrivingLessonType,
      pauseBetweenDrivingLessonsMins,
      allowOverbooking = false,
      ...rest
    } = dto;
    // The stored property named `maxDrivingLessonsPerWeek` is interpreted as `maxLessonUnitsPerWeek`
    // in the mobile-app! We duplicate that behavior here ...
    const maxLessonUnitsPerWeek = maxDrivingLessonsPerWeek;
    const maxLessonUnitsPerDay = maxDrivingLessonLUsPerDay ?? Number.POSITIVE_INFINITY;
    const drivingLessonDuration = preferredDurationMinsPerDrivingLessonType.normal[0];
    const pauseDuration = pauseBetweenDrivingLessonsMins;
    return {
      data: {
        id,
        maxLessonUnitsPerWeek,
        maxLessonUnitsPerDay,
        drivingLessonDuration,
        pauseDuration,
        allowOverbooking,
        ...rest,
      },
    };
  }

  async update(
    _: string,
    { id, data, previousData }: UpdateParams<RecommendationSettings>,
  ): Promise<GetOneResult<RecommendationSettings>> {
    const { maxLessonUnitsPerWeek, maxLessonUnitsPerDay, drivingLessonDuration, pauseDuration, ...rest } = {
      ...previousData,
      ...data,
    };
    // Reverse map ...
    const maxDrivingLessonsPerWeek = maxLessonUnitsPerWeek;
    const maxDrivingLessonLUsPerDay = maxLessonUnitsPerDay === Number.POSITIVE_INFINITY ? null : maxLessonUnitsPerDay;
    const preferredDurationMinsPerDrivingLessonType = { normal: [drivingLessonDuration] };
    const pauseBetweenDrivingLessonsMins = pauseDuration;
    const dto = schemas.RecommendationSettingsDto.parse({
      maxDrivingLessonsPerWeek,
      maxDrivingLessonLUsPerDay,
      preferredDurationMinsPerDrivingLessonType,
      pauseBetweenDrivingLessonsMins,
      ...rest,
    });
    const serverClient = await getAuthenticatedServerClient();
    await serverClient.setInstructorRecommendationSettings(dto, { params: { id } });
    return {
      data: { maxLessonUnitsPerWeek, maxLessonUnitsPerDay, drivingLessonDuration, pauseDuration, ...rest },
    };
  }
}

export const instructorRecommendationSettingsProvider = new InstructorRecommendationSettingsProvider();
